import { ApolloLink } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';

/**
 * GraphQL operation type as it appears in a request document.
 */
type GraphQlOperationType = 'mutation' | 'query' | 'subscription';

/**
 * Shape of the JSON GraphQL request body fields this module reads/writes.
 */
type GraphQlRequestBody = {
  /**
   * The operation name Apollo always sends, present on APQ cache-hits too.
   */
  operationName?: string;
  /**
   * Top-level operation type the Datadog RUM SDK reads when the query is absent.
   */
  operationType?: string;
  /**
   * The GraphQL document; absent on APQ cache-hits, present on full-query requests.
   */
  query?: string;
};

/**
 * Whether to enrich outgoing GraphQL request bodies with a top-level
 * `operationType` for Datadog RUM. Off unless explicitly enabled so the feature
 * can be rolled out per-environment: enable it in a lower environment first to
 * confirm the backend accepts the extra body field and RUM populates
 * `@resource.graphql.operationType`, then enable it in production.
 */
const isOperationTypeEnrichmentEnabled = (): boolean =>
  process.env.REACT_APP_DATADOG_GRAPHQL_OPERATION_TYPE_ENABLED === 'true';

const operationTypesByName = new Map<string, GraphQlOperationType>();

/**
 * Records each operation's type (query/mutation/subscription) keyed by its name.
 *
 * Place this ahead of the terminating HTTP link. It only observes and forwards;
 * it never mutates the operation. Pairs with `operationTypeFetch`, which reads
 * the recorded type back to enrich the outgoing request body.
 */
export const operationTypeLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);

  if (definition.kind === 'OperationDefinition' && operation.operationName) {
    operationTypesByName.set(operation.operationName, definition.operation);
  }

  return forward(operation);
});

/**
 * Returns `body` with a top-level `operationType` added when it is missing and
 * the operation's type is known. Returns the original string unchanged on any
 * parse failure or when nothing needs adding.
 */
const enrichBodyWithOperationType = (body: string): string => {
  let parsed: GraphQlRequestBody;

  try {
    parsed = JSON.parse(body);
  } catch {
    return body;
  }

  /*
   * Nothing to add: not an object, a full-query request (the Datadog RUM SDK
   * derives the type from the query string itself), type already present, or no
   * name to key on. Scoping to query-less bodies limits enrichment to exactly
   * the APQ cache-hits that need it.
   */
  if (!parsed || typeof parsed !== 'object' || parsed.query || parsed.operationType || !parsed.operationName) {
    return body;
  }

  const operationType = operationTypesByName.get(parsed.operationName);
  if (!operationType) {
    return body;
  }

  return JSON.stringify({ ...parsed, operationType });
};

/**
 * A `fetch` wrapper that adds a top-level `operationType` to the JSON request
 * body when it is absent.
 *
 * Datadog RUM derives `@resource.graphql.operationType` by parsing the query
 * string, but APQ cache-hits omit the query. The browser-sdk (v6.31.0+) falls
 * back to a top-level `operationType` body field, so supplying it here restores
 * per-operation-type traceability under APQ. Multipart upload requests (FormData
 * bodies) are left untouched — they are not APQ cache-hits.
 *
 * Gated by `isOperationTypeEnrichmentEnabled`: when disabled it is a transparent
 * passthrough to the global `fetch`, adding nothing to the request body.
 *
 * @param input The fetch resource (URL or Request), forwarded unchanged.
 * @param init The fetch init; a copy with an enriched JSON body is forwarded.
 * @returns The forwarded global `fetch` call.
 */
export const operationTypeFetch: typeof fetch = (input, init) => {
  if (init && isOperationTypeEnrichmentEnabled() && typeof init.body === 'string') {
    return fetch(input, { ...init, body: enrichBodyWithOperationType(init.body) });
  }

  return fetch(input, init);
};
